import { useValidateMetadata, useUpdateDocumentMetadata, documentsKeys } from '@cs-cms/state-management';
import {
  CpsDocumentMetadata,
  ValidateMetadataRequest,
  CONTENT_TYPE_FIELD,
  FILE_FIELD,
  UpdateDocumentMetadata,
  DOC_ID_FIELD
} from '@cs-cms/types';
import { useQueryClient } from '@tanstack/react-query';
import { ExtendedUploadFileInfo } from '../../types/ExtendedUploadFileInfo';
import { useChunkedUpload } from './useChunkUpload';
import { useSmallFileUpload } from './useSmallFileUpload';

interface UseDocumentSubmissionParams {
  indexId: number;
  locale: string;
  isNewDocument: boolean;
  contentTypeFields: any[];
  allowsMultiFileUpload: boolean;
  existingDocument?: any;
  existingDocumentETag?: string;
  existingDocumentFileName?: string;
  transformMetadataForSubmit?: (metadata: CpsDocumentMetadata) => CpsDocumentMetadata;
}

interface FileWithMetadata {
  file?: ExtendedUploadFileInfo;
  perFileMetadata?: Record<string, any>;
}

const PERMISSIONS_TO_VALIDATE_FOR = {
  NONE: 0,
  CREATE: 1,
  UPDATE: 2
};

export const useDocumentSubmission = ({
  indexId,
  locale,
  isNewDocument,
  contentTypeFields,
  allowsMultiFileUpload,
  existingDocument,
  existingDocumentETag,
  existingDocumentFileName,
  transformMetadataForSubmit
}: UseDocumentSubmissionParams) => {
  const queryClient = useQueryClient();
  const { mutateAsync: validateMetadata } = useValidateMetadata();
  const { mutate: mutateUpdateDocumentMetadata } = useUpdateDocumentMetadata();
  const { uploadSmallFile, isLoading: isSmallFileUploading } = useSmallFileUpload({ isNewDocument });
  const {
    uploadLargeFile,
    isLargeFile,
    isLoading: isChunkedUploading
  } = useChunkedUpload({ selectedIndexId: indexId, isNewDocument });

  const applyMetadataTransform = (metadata: CpsDocumentMetadata): CpsDocumentMetadata => {
    if (transformMetadataForSubmit === undefined) {
      return metadata;
    }
    return transformMetadataForSubmit(metadata);
  };

  const extractMetadata = (dataItem: any): CpsDocumentMetadata => {
    const raw = Object.keys(dataItem)
      .filter(key => contentTypeFields.some(f => f.name === key))
      .reduce((obj: CpsDocumentMetadata, key) => {
        obj[key] = dataItem[key];
        return obj;
      }, {});

    return applyMetadataTransform(raw);
  };

  const createValidateDto = (dataItem: any, metadata: CpsDocumentMetadata, docId?: string): ValidateMetadataRequest => {
    return {
      docId,
      metadata,
      indexId,
      validateFor: isNewDocument ? PERMISSIONS_TO_VALIDATE_FOR.CREATE : PERMISSIONS_TO_VALIDATE_FOR.UPDATE,
      ctName: isNewDocument ? dataItem[CONTENT_TYPE_FIELD].name : existingDocument[CONTENT_TYPE_FIELD],
      locale
    };
  };

  const getFileFromData = (dataItem: any): File | undefined => {
    if (isNewDocument) {
      return dataItem.file?.[0]?.getRawFile?.();
    }
    return dataItem[FILE_FIELD]?.[0]?.getRawFile?.();
  };

  const handleMetadataOnlyUpdate = (metadata: CpsDocumentMetadata, onSuccess?: () => void): void => {
    if (!isNewDocument && existingDocument && existingDocumentFileName) {
      const metadataDto: UpdateDocumentMetadata = {
        ctName: existingDocument[CONTENT_TYPE_FIELD],
        documentId: existingDocument[DOC_ID_FIELD],
        fileName: existingDocumentFileName,
        searchIndexId: indexId,
        metadata
      };

      mutateUpdateDocumentMetadata(
        { dto: metadataDto, etag: existingDocumentETag },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries({
              queryKey: documentsKeys.documentMetadata({
                documentId: existingDocument[DOC_ID_FIELD],
                ctName: existingDocument[CONTENT_TYPE_FIELD],
                indexId
              })
            });
            onSuccess?.();
          }
        }
      );
    }
  };

  const handleSingleFileSubmission = async (
    dataItem: any,
    metadata: CpsDocumentMetadata,
    onSuccess?: () => void,
    retry = false
  ): Promise<void> => {
    const file = getFileFromData(dataItem);
    const ctName = isNewDocument ? dataItem[CONTENT_TYPE_FIELD].name : existingDocument[CONTENT_TYPE_FIELD];

    if (!file) {
      handleMetadataOnlyUpdate(metadata, onSuccess);
      return;
    }

    if (isLargeFile(file.size)) {
      await uploadLargeFile({
        file,
        ctName,
        documentId: isNewDocument ? undefined : existingDocument[DOC_ID_FIELD],
        metadata
      });

      await queryClient.invalidateQueries({
        queryKey: documentsKeys.documentMetadata({
          documentId: isNewDocument ? dataItem[DOC_ID_FIELD] : existingDocument[DOC_ID_FIELD],
          ctName,
          indexId
        })
      });

      onSuccess?.();
    } else {
      uploadSmallFile(
        {
          file,
          ctName,
          documentId: isNewDocument ? undefined : existingDocument[DOC_ID_FIELD],
          metadata,
          etag: existingDocumentETag,
          retry
        },
        {
          onSuccess: async () => {
            if (!isNewDocument) {
              await queryClient.invalidateQueries({
                queryKey: documentsKeys.documentMetadata({
                  documentId: existingDocument[DOC_ID_FIELD],
                  ctName: existingDocument[CONTENT_TYPE_FIELD],
                  indexId
                })
              });
            }
            onSuccess?.();
          }
        }
      );
    }
  };

  const validateSingleFileMetadata = async (combinedMetadata: CpsDocumentMetadata, ctName: string): Promise<any[]> => {
    const validateDto: ValidateMetadataRequest = {
      docId: undefined,
      metadata: combinedMetadata,
      indexId,
      validateFor: PERMISSIONS_TO_VALIDATE_FOR.CREATE,
      ctName,
      locale
    };

    try {
      return await validateMetadata({ dto: validateDto });
    } catch {
      return [{ field: 'validation', message: 'Validation request failed' }];
    }
  };

  const handleMultipleFileSubmissions = async (
    filesWithMetadata: FileWithMetadata[],
    sharedMetadata: CpsDocumentMetadata,
    ctName: string,
    onSuccess?: () => void,
    retry = false
  ): Promise<void> => {
    for (const fileWithMeta of filesWithMetadata) {
      const file = fileWithMeta.file?.getRawFile?.();
      if (!file) {
        continue;
      }

      const combinedMetadata = applyMetadataTransform({
        ...sharedMetadata,
        ...fileWithMeta.perFileMetadata
      });

      if (isLargeFile(file.size)) {
        await uploadLargeFile({
          file,
          ctName,
          metadata: combinedMetadata
        });
      } else {
        uploadSmallFile({
          file,
          ctName,
          metadata: combinedMetadata,
          retry
        });
      }
    }

    onSuccess?.();
  };

  const submitForm = async (dataItem: any, onSuccess?: () => void, retry = false): Promise<void> => {
    const sharedMetadata = extractMetadata(dataItem);
    const isMultiFileCreation = isNewDocument && allowsMultiFileUpload;

    if (isMultiFileCreation) {
      const filesWithMetadata: FileWithMetadata[] = dataItem[FILE_FIELD];
      const ctName = dataItem[CONTENT_TYPE_FIELD].name;

      const validationPromises = filesWithMetadata.map(fileWithMeta => {
        const combinedMetadata = applyMetadataTransform({
          ...sharedMetadata,
          ...fileWithMeta.perFileMetadata
        });
        return validateSingleFileMetadata(combinedMetadata, ctName);
      });

      const allValidationResults = await Promise.all(validationPromises);
      const hasErrors = allValidationResults.some(errors => errors.length > 0);

      if (hasErrors) {
        return;
      }

      await handleMultipleFileSubmissions(filesWithMetadata, sharedMetadata, ctName, onSuccess, retry);
    } else {
      const fileArray = dataItem[FILE_FIELD];
      const perFileMetadata = fileArray?.[0]?.perFileMetadata || {};
      const finalMetadata = applyMetadataTransform({
        ...sharedMetadata,
        ...perFileMetadata
      });

      const validateDto = createValidateDto(
        dataItem,
        finalMetadata,
        isNewDocument ? undefined : existingDocument[DOC_ID_FIELD]
      );

      validateMetadata(
        { dto: validateDto },
        {
          onSuccess: async validationErrors => {
            if (validationErrors.length > 0) {
              return;
            }

            await handleSingleFileSubmission(dataItem, finalMetadata, onSuccess, retry);
          }
        }
      );
    }
  };

  return {
    submitForm,
    isLoading: isSmallFileUploading || isChunkedUploading
  };
};
